import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  BarChart3, MessagesSquare, Users, Store, MessageCircle, UserCog,
  Trash2, Plus, ArrowLeft, ShieldCheck, X, Eye, EyeOff, Inbox, Mail, Phone, Check as CheckIcon,
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="bg-white border border-[#E5E0D8] rounded-xl p-5">
    <div className="flex items-center gap-3">
      <span className="w-10 h-10 rounded-lg bg-[#F4EFE6] flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#E63946]" />
      </span>
      <div>
        <p className="text-2xl font-display font-black text-[#2B2B2B] leading-none">{value}</p>
        <p className="text-xs font-mono text-[#5C5C5C] mt-1">{label}</p>
      </div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { customer, loading } = useAuth();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [openConv, setOpenConv] = useState(null);
  const [users, setUsers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [applications, setApplications] = useState([]);
  const [newStaff, setNewStaff] = useState({ name: "", email: "", password: "" });
  const [creating, setCreating] = useState(false);

  const isAdmin = customer && (customer.role === "super_admin" || customer.role === "staff");
  const isSuperAdmin = customer && customer.role === "super_admin";

  const loadStats = useCallback(async () => {
    try { const r = await api.get("/admin/stats"); setStats(r.data); } catch { /* noop */ }
  }, []);
  const loadConversations = useCallback(async () => {
    try { const r = await api.get("/admin/conversations"); setConversations(r.data); } catch { /* noop */ }
  }, []);
  const loadUsers = useCallback(async () => {
    try { const r = await api.get("/admin/users"); setUsers(r.data); } catch { /* noop */ }
  }, []);
  const loadRestaurants = useCallback(async () => {
    try { const r = await api.get("/admin/restaurants"); setRestaurants(r.data); } catch { /* noop */ }
  }, []);
  const loadApplications = useCallback(async () => {
    try { const r = await api.get("/admin/partner-applications"); setApplications(r.data); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!isAdmin) { navigate("/login"); return; }
    loadStats();
    loadConversations();
    loadApplications();
    if (isSuperAdmin) { loadUsers(); loadRestaurants(); }
  }, [loading, isAdmin, isSuperAdmin, navigate, loadStats, loadConversations, loadApplications, loadUsers, loadRestaurants]);

  const openConversation = async (sid) => {
    try { const r = await api.get(`/admin/conversations/${sid}`); setOpenConv(r.data); }
    catch { toast.error("Erreur de chargement"); }
  };

  const createStaff = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/users", newStaff);
      toast.success("Utilisateur créé");
      setNewStaff({ name: "", email: "", password: "" });
      loadUsers();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setCreating(false); }
  };

  const deleteStaff = async (uid) => {
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success("Utilisateur supprimé");
      loadUsers();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const setRestaurantStatus = async (rid, status) => {
    try {
      await api.put(`/admin/restaurants/${rid}/status`, { status });
      toast.success(status === "published" ? "Restaurant publié" : "Restaurant retiré");
      loadRestaurants();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const setApplicationStatus = async (aid, status) => {
    try {
      await api.put(`/admin/partner-applications/${aid}/status`, { status });
      const labels = {
        contacted: "Marqué comme contacté",
        approved: "Candidature approuvée",
        rejected: "Candidature rejetée",
        pending: "Remis en attente",
      };
      toast.success(labels[status] || "OK");
      loadApplications();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  if (loading || !isAdmin) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] font-mono text-[#5C5C5C]">Chargement…</div>;
  }

  const tabs = [
    { id: "stats", label: "Statistiques", icon: BarChart3 },
    { id: "applications", label: "Candidatures", icon: Inbox },
    { id: "conversations", label: "Conversations", icon: MessagesSquare },
    ...(isSuperAdmin ? [{ id: "restaurants", label: "Restaurants", icon: Store }] : []),
    ...(isSuperAdmin ? [{ id: "users", label: "Utilisateurs", icon: Users }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Header />
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-10">
        <button data-testid="admin-back-btn" onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm font-mono text-[#5C5C5C] hover:text-[#E63946] transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour au site
        </button>

        <div className="flex items-center gap-3 mb-2">
          <span className="w-11 h-11 rounded-full bg-[#E63946] flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-white" />
          </span>
          <div>
            <h1 className="text-3xl font-display font-black tracking-tighter text-[#2B2B2B]">Administration Vytali</h1>
            <p className="text-sm font-mono text-[#5C5C5C]">
              {isSuperAdmin ? "Super Administrateur" : "Membre du staff"} · {customer.email}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mt-8 mb-6 border-b border-[#E5E0D8]">
          {tabs.map((tb) => (
            <button key={tb.id} data-testid={`admin-tab-${tb.id}`} onClick={() => setTab(tb.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 -mb-px transition-colors ${
                tab === tb.id ? "border-[#E63946] text-[#E63946]" : "border-transparent text-[#5C5C5C] hover:text-[#2B2B2B]"
              }`}>
              <tb.icon className="w-4 h-4" /> {tb.label}
            </button>
          ))}
        </div>

        {/* Stats */}
        {tab === "stats" && (
          <div data-testid="admin-stats-panel" className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <StatCard testid="stat-restaurants" icon={Store} label="Restaurants publiés" value={stats?.restaurants ?? "…"} />
            <StatCard testid="stat-pending" icon={EyeOff} label="En attente de validation" value={stats?.pending_restaurants ?? "…"} />
            <StatCard testid="stat-conversations" icon={MessagesSquare} label="Conversations chat" value={stats?.conversations ?? "…"} />
            <StatCard testid="stat-messages" icon={MessageCircle} label="Messages échangés" value={stats?.messages ?? "…"} />
            <StatCard testid="stat-staff" icon={UserCog} label="Comptes admin" value={stats?.staff_users ?? "…"} />
            <StatCard testid="stat-customers" icon={Users} label="Clients inscrits" value={stats?.customers ?? "…"} />
          </div>
        )}

        {/* Applications (Partenaires) */}
        {tab === "applications" && (
          <div data-testid="admin-applications-panel">
            <p className="text-sm font-mono text-[#5C5C5C] mb-4">
              Candidatures reçues via la page « Devenir Partenaire » — {applications.length} au total.
            </p>
            {applications.length === 0 ? (
              <p className="font-mono text-sm text-[#5C5C5C] py-8 text-center">
                Aucune candidature pour le moment.
              </p>
            ) : (
              <div className="space-y-3">
                {applications.map((a) => {
                  const badge = {
                    pending:   { label: "En attente",  bg: "bg-[#F4A261]" },
                    contacted: { label: "Contacté",    bg: "bg-[#264653]" },
                    approved:  { label: "Approuvé",    bg: "bg-[#4A5D23]" },
                    rejected:  { label: "Rejeté",      bg: "bg-[#5C5C5C]" },
                  }[a.status] || { label: a.status, bg: "bg-[#5C5C5C]" };
                  return (
                    <div key={a.id} data-testid={`application-row-${a.id}`}
                      className="bg-white border border-[#E5E0D8] rounded-xl p-5">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-bold text-base text-[#2B2B2B]">{a.business_name}</p>
                            <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full text-white ${badge.bg}`}>
                              {badge.label}
                            </span>
                            <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F4EFE6] text-[#2B2B2B]">
                              {a.business_type}
                            </span>
                          </div>
                          <div className="mt-2 text-sm text-[#5C5C5C] space-y-1">
                            <p>👤 {a.contact_name}</p>
                            <p className="flex items-center gap-2 flex-wrap">
                              <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1.5 hover:text-[#E63946] font-mono">
                                <Mail className="w-3.5 h-3.5" /> {a.email}
                              </a>
                              <a href={`tel:${a.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#E63946] font-mono">
                                <Phone className="w-3.5 h-3.5" /> {a.phone}
                              </a>
                            </p>
                            {(a.address || a.city) && (
                              <p className="font-mono text-xs">📍 {[a.address, a.city].filter(Boolean).join(", ")}</p>
                            )}
                            {a.website && (
                              <p className="font-mono text-xs">🌐 {a.website}</p>
                            )}
                            {a.message && (
                              <p className="mt-2 p-3 rounded-lg bg-[#F4EFE6] text-[#2B2B2B] text-sm italic">
                                « {a.message} »
                              </p>
                            )}
                          </div>
                          <p className="mt-2 text-[11px] font-mono text-[#9C9C9C]">
                            {fmtDate(a.created_at)}
                          </p>
                        </div>

                        <div className="flex flex-col gap-2 shrink-0 min-w-[150px]">
                          {a.status !== "contacted" && a.status !== "approved" && (
                            <button data-testid={`app-contact-${a.id}`}
                              onClick={() => setApplicationStatus(a.id, "contacted")}
                              className="flex items-center justify-center gap-1.5 px-3 h-9 rounded-full bg-[#264653] text-white font-bold text-xs hover:scale-[1.03] transition-transform">
                              <Phone className="w-3.5 h-3.5" /> Contacté
                            </button>
                          )}
                          {a.status !== "approved" && (
                            <button data-testid={`app-approve-${a.id}`}
                              onClick={() => setApplicationStatus(a.id, "approved")}
                              className="flex items-center justify-center gap-1.5 px-3 h-9 rounded-full bg-[#4A5D23] text-white font-bold text-xs hover:scale-[1.03] transition-transform">
                              <CheckIcon className="w-3.5 h-3.5" /> Approuver
                            </button>
                          )}
                          {a.status !== "rejected" && (
                            <button data-testid={`app-reject-${a.id}`}
                              onClick={() => setApplicationStatus(a.id, "rejected")}
                              className="flex items-center justify-center gap-1.5 px-3 h-9 rounded-full bg-white border border-[#E5E0D8] text-[#5C5C5C] font-bold text-xs hover:bg-[#F4EFE6] transition-colors">
                              <X className="w-3.5 h-3.5" /> Rejeter
                            </button>
                          )}
                          {a.status !== "pending" && (
                            <button data-testid={`app-reset-${a.id}`}
                              onClick={() => setApplicationStatus(a.id, "pending")}
                              className="text-[11px] font-mono text-[#9C9C9C] hover:text-[#E63946] underline">
                              Remettre en attente
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Conversations */}
        {tab === "conversations" && (
          <div data-testid="admin-conversations-panel">
            {conversations.length === 0 ? (
              <p className="font-mono text-sm text-[#5C5C5C] py-8 text-center">Aucune conversation pour le moment.</p>
            ) : (
              <div className="space-y-2">
                {conversations.map((c) => (
                  <button key={c.session_id} data-testid={`conversation-row-${c.session_id}`}
                    onClick={() => openConversation(c.session_id)}
                    className="w-full text-left bg-white border border-[#E5E0D8] rounded-xl p-4 hover:border-[#E63946] transition-colors">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-xs font-mono text-[#9C9C9C]">{c.session_id}</span>
                      <span className="text-xs font-mono text-[#5C5C5C]">{fmtDate(c.last_at)}</span>
                    </div>
                    <p className="text-sm text-[#2B2B2B] mt-1 line-clamp-1">{c.last_message}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs font-mono bg-[#F4EFE6] px-2 py-0.5 rounded-full">{c.message_count} messages</span>
                      <span className="text-xs font-mono bg-[#F4EFE6] px-2 py-0.5 rounded-full uppercase">{c.lang}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Restaurants */}
        {tab === "restaurants" && isSuperAdmin && (
          <div data-testid="admin-restaurants-panel">
            <p className="text-sm font-mono text-[#5C5C5C] mb-4">
              Les restaurants « en attente » sont invisibles sur le site tant que tu ne les as pas publiés.
            </p>
            {restaurants.length === 0 ? (
              <p className="font-mono text-sm text-[#5C5C5C] py-8 text-center">Aucun restaurant.</p>
            ) : (
              <div className="space-y-2">
                {restaurants.map((r) => (
                  <div key={r.id} data-testid={`admin-restaurant-row-${r.id}`}
                    className="flex items-center justify-between gap-3 bg-white border border-[#E5E0D8] rounded-xl p-4">
                    <div className="flex items-center gap-3 min-w-0">
                      {r.image ? (
                        <img src={r.image} alt={r.name} className="w-12 h-12 rounded-lg object-cover shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      ) : (
                        <span className="w-12 h-12 rounded-lg bg-[#F4EFE6] flex items-center justify-center shrink-0"><Store className="w-5 h-5 text-[#9C9C9C]" /></span>
                      )}
                      <div className="min-w-0">
                        <p className="font-bold text-sm text-[#2B2B2B] truncate">{r.name}</p>
                        <p className="text-xs font-mono text-[#5C5C5C] truncate">{r.cuisine}{r.city ? ` · ${r.city}` : ""}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span data-testid={`restaurant-status-${r.id}`}
                        className={`text-xs font-mono px-2 py-1 rounded-full ${r.status === "pending" ? "bg-[#F4A261] text-white" : "bg-[#4A5D23] text-white"}`}>
                        {r.status === "pending" ? "En attente" : "Publié"}
                      </span>
                      {r.status === "pending" ? (
                        <button data-testid={`publish-restaurant-${r.id}`} onClick={() => setRestaurantStatus(r.id, "published")}
                          className="flex items-center gap-1.5 px-3 h-9 rounded-full bg-[#E63946] text-white font-bold text-xs hover:scale-[1.03] transition-transform">
                          <Eye className="w-4 h-4" /> Publier
                        </button>
                      ) : (
                        <button data-testid={`unpublish-restaurant-${r.id}`} onClick={() => setRestaurantStatus(r.id, "pending")}
                          className="flex items-center gap-1.5 px-3 h-9 rounded-full bg-white border border-[#E5E0D8] text-[#2B2B2B] font-bold text-xs hover:bg-[#F4EFE6] transition-colors">
                          <EyeOff className="w-4 h-4" /> Retirer
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Users */}
        {tab === "users" && isSuperAdmin && (
          <div data-testid="admin-users-panel" className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-[#E5E0D8] rounded-xl p-6">
              <h2 className="text-lg font-display font-bold mb-4 flex items-center gap-2"><Plus className="w-5 h-5 text-[#E63946]" /> Nouvel utilisateur</h2>
              <form onSubmit={createStaff} className="space-y-3">
                <input data-testid="staff-name-input" required value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
                  placeholder="Nom complet" className="w-full h-11 px-4 bg-[#FDFBF7] border border-[#E5E0D8] rounded-lg outline-none focus:border-[#E63946] text-sm" />
                <input data-testid="staff-email-input" type="email" required value={newStaff.email} onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                  placeholder="Courriel" className="w-full h-11 px-4 bg-[#FDFBF7] border border-[#E5E0D8] rounded-lg outline-none focus:border-[#E63946] text-sm" />
                <input data-testid="staff-password-input" type="text" required value={newStaff.password} onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })}
                  placeholder="Mot de passe temporaire" className="w-full h-11 px-4 bg-[#FDFBF7] border border-[#E5E0D8] rounded-lg outline-none focus:border-[#E63946] text-sm" />
                <button data-testid="create-staff-btn" type="submit" disabled={creating}
                  className="w-full h-11 rounded-full bg-[#E63946] text-white font-bold hover:scale-[1.02] transition-transform disabled:opacity-50">
                  {creating ? "…" : "Créer l'utilisateur"}
                </button>
              </form>
              <p className="text-xs font-mono text-[#9C9C9C] mt-3">Les nouveaux utilisateurs ont le rôle « staff » : ils peuvent voir les stats et les conversations, mais pas gérer les utilisateurs.</p>
            </div>

            <div>
              <h2 className="text-lg font-display font-bold mb-4">Équipe ({users.length})</h2>
              <div className="space-y-2">
                {users.map((u) => (
                  <div key={u.user_id} data-testid={`user-row-${u.user_id}`}
                    className="flex items-center justify-between gap-3 bg-white border border-[#E5E0D8] rounded-xl p-4">
                    <div className="min-w-0">
                      <p className="font-bold text-sm text-[#2B2B2B] truncate">{u.name || u.email}</p>
                      <p className="text-xs font-mono text-[#5C5C5C] truncate">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs font-mono px-2 py-1 rounded-full ${u.role === "super_admin" ? "bg-[#E63946] text-white" : "bg-[#F4EFE6] text-[#2B2B2B]"}`}>
                        {u.role === "super_admin" ? "Super Admin" : "Staff"}
                      </span>
                      {u.role !== "super_admin" && (
                        <button data-testid={`delete-user-${u.user_id}`} onClick={() => deleteStaff(u.user_id)}
                          className="p-2 rounded-lg text-[#E63946] hover:bg-[#F4EFE6] transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Conversation transcript modal */}
      {openConv && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setOpenConv(null)}>
          <div data-testid="conversation-modal" className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-[#E5E0D8]">
              <span className="font-mono text-sm text-[#5C5C5C]">{openConv.session_id}</span>
              <button data-testid="close-conversation-modal" onClick={() => setOpenConv(null)} className="p-1 rounded-lg hover:bg-[#F4EFE6]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto">
              {openConv.messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${m.role === "user" ? "bg-[#E63946] text-white" : "bg-[#F4EFE6] text-[#2B2B2B]"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
